<template>
  <group-changes-detection v-if="branch"
    :value="[branch.landsOfWork, branch.businessProfileUrl, branch.active, branch.name, branch.branchType, branch.address, branch.agents, branchHours]"
    :emptyValues="[emptyLands, null]"
    :hasChanges.sync="hasChanges">
    <v-row class="mx-2" :class="{ 'has-changes': hasChanges }">
      <v-col cols="12" md="6">
        <v-row>
          <v-col cols="12">
            <card-edit-data-wrapper :title="$i18n.useAddBranchDetails.DATA">
              <template #actions>
                <fp-checkbox class="d-inline-block" label="Aktywna" v-model="branch.active"/>
              </template>
              <v-layout no-gutters wrap>
                <v-flex xs12>
                  <fp-chip-radio
                      v-model="branch.branchType"
                      :items="branchTypes"
                      item-key="type"
                      item-name="name_pl"/>
                </v-flex>
                <fp-input xs12 :label="$i18n.useAddBranchDetails.NAME" v-model="branch.name"/>
                <fp-loader size="40px" v-if="!branch"/>
                <edit-user-address-data v-model="branch.address"/>
                <fp-input 
                  v-if="branch.branchType !== BranchType.CERTIFIED && !$env.isAppDomain().NESTO_RO"
                   v-model="branch.businessProfileUrl"
                  :label="$i18n.useAddBranchModal.BUSINESS_CARD_URL"
                  branch-business-card
                  xs12
                />
              </v-layout>
            </card-edit-data-wrapper>
          </v-col>
          <v-col cols="12">
            <card-edit-data-wrapper :title="$i18n.useAddBranchDetails.HOURS_OF_WORK">
              <edit-user-business-hours v-model="branchHours"/>
            </card-edit-data-wrapper>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="6">
        <v-row>
          <v-col cols="12">
            <card-edit-data-wrapper :title="$i18n.useAddBranchDetails.USERS">
              <user-autocomplete
                  :multiple="true"
                  :existing-items.sync="branch.agents"
                  item-text="displayNameWithOrganisationAndEmail"
                  v-model="branch.agentIds"
                  clear-after-select
                />
            </card-edit-data-wrapper>
          </v-col>
          <v-col cols="12">
            <card-edit-data-wrapper :title="$i18n.useAddBranchDetails.LANDS_OF_WORK">
              <edit-user-lands-data v-model="branch.landsOfWork"/>
            </card-edit-data-wrapper>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <experts-list class="ml-0 mr-0" :items="branch.agents" :readonly="true" height="auto"/>
      </v-col>
      <div v-if="hasChanges" class="bt fixed-bottom-bar pa-4">
        <fp-btn :click="() => saveChanges()" success>
          {{ $i18n.useButtonCommons.SAVE_CHANGES }}
        </fp-btn>
        <fp-btn :click="() => undoChanges()">
          {{ $i18n.useButtonCommons.CANCEL_CHANGES }}
        </fp-btn>
      </div>
    </v-row>
  </group-changes-detection>
</template>
<script lang="ts">
import {Vue, Component} from 'vue-property-decorator';
import {Branch, BranchLandsDto, BranchTypeModel} from '@/modules/branches/models/Branch';
import BranchApi from '@/modules/branches/services/BranchApi';
import NavigationHeader from '@/components/NavigationHeader/NavigationHeader.vue';
import GroupChangesDetection from '@/components/commons/GroupChangesDetection.vue';
import CardEditDataWrapper from '@/modules/user/editUser/CardEditDataWrapper.vue';
import EditUserBusinessHours from '@/modules/user/editUser/EditUserBusinessHours.vue';
import EditUserAddressData from '@/modules/user/editUser/EditUserAddressData.vue';
import {Address} from '@/modules/multiForm/shared/MultiformModels';
import BranchTypes from '@/modules/branches/services/BranchTypes';
import {userBusinessHours, UserBusinessHoursElement} from '@/models/UserBusinessHoursElement';
import FpChipRadio from '@/components/inputs/FpChipRadio.vue';
import ClientAutocomplete from '@/components/inputs/ClientAutocomplete.vue';
import UserAutocomplete from '@/components/inputs/autocompletes/UserAutocomplete.vue';
import ExpertsList from '@/modules/adminPanel/views/ExpertsList.vue';
import EditUserLandsData from '@/modules/user/editUser/EditUserLandsData.vue';
import { LandOfWork } from '@/models/LandOfWork';
import { cloneDeep, differenceBy, isEqual } from 'lodash-es';
import { UserListElement } from '@/models/ppl/UserListElement';
import User from '@/models/user/User';
import UserApi from '@/modules/user/services/UserApi';
import { Quizzes } from '@/models/user/Quizzes';
import { NavigationGuardNext, Route } from 'vue-router';
import { Routes } from '@/router/Routes';
import SaveChangesModal from '@/modules/adminPanel/views/DataSaving/SaveChangesModal.vue';
import UndoChangesModal from '@/modules/adminPanel/views/DataSaving/UndoChangesModal.vue';
import {BranchType} from '@/modules/branches/enums/BranchType';
import EnvironmentService from '@/env/EnvironmentService';

@Component({
  components: {
    ExpertsList,
    UserAutocomplete,
    ClientAutocomplete,
    FpChipRadio,
    GroupChangesDetection,
    NavigationHeader,
    CardEditDataWrapper,
    EditUserBusinessHours,
    EditUserAddressData,
    EditUserLandsData,
  },
})
export default class BranchDetails extends Vue {
  public branch: Branch | null = null;
  public branchAddress: Address | null = null;
  public branchHours: UserBusinessHoursElement[] | null = null;
  public branchTypes: BranchTypeModel[] = BranchTypes;

  private hasChanges: boolean = false;
  private branchClone: Branch | null = null;
  private branchHoursClone: UserBusinessHoursElement[] | null = null;
  private emptyLands: LandOfWork[] = [new LandOfWork(),];
  private unregisterGuard: Nullable<Function> = null;
  public BranchType = BranchType;

  public get branchId(): number {
    return Number(this.$route.params.id);
  }

  public async mounted() {
    await this.fetchData();
    this.unregisterGuard = this.$router.beforeEach(async(to: Route, from: Route, next: NavigationGuardNext) => {
      if (this.hasChanges &&
        from.name === Routes.BRANCHES.BRANCH_DETAILS &&
        to.name !== Routes.BRANCHES.BRANCH_DETAILS) {
        const modalResponse = await this.openSaveChangesModal();
        if (modalResponse) {
          await this.saveChanges();
          next();
        } else if (modalResponse === false) {
          await this.undoChanges();
          next();
        }

        return;
      }

      next();
    });
  }

  async fetchData() {
    try {
      const response = await BranchApi.getBranch(this.branchId);
      this.branch = response;
      if (response.hours.length === 0) {
        this.branchHours = userBusinessHours;
        await this.updateBranchHours();
      } else {
        this.branchHours = response.hours.map(item => new UserBusinessHoursElement(item));
      }
      await this.getBranchLands();
      this.setClones();
    } catch (e) {
      this.errorHandler(e, 'Nie można załadować klienta');
    }
  }

  async updateBranch() {
    try {
      if (this.branch) {
        if (this.branch.branchType === BranchType.CERTIFIED || EnvironmentService.Environment.isAppDomain().NESTO_RO) {
          this.branch.businessProfileUrl = null;
        }
        const response = await BranchApi.updateBranch(this.branchId,this.branch);
        this.$snackbarService.openSuccessSnackbar('Pomyślnie zaktualizowane dane placówki.');
      }
    } catch (e) {
      this.errorHandler(e);
    }
  }

  async updateBranchHours() {
    try {
      await BranchApi.updateBranchHours(this.branchId,this.branchHours!);
    } catch (e) {
      this.errorHandler(e);
    }
  }

  async getBranchLands() {
    try {
      const response = await BranchApi.getBranchLands(this.branchId);
      if (response?.lands && this.branch) {
        this.branch.landsOfWork = response.lands.map(land => new LandOfWork(land));
      }
    } catch (e) {
      this.errorHandler(e);
    }
  }

  async updateBranchLands() {
    try {
      const branchLands: BranchLandsDto = {
        branchId: this.branch!.id,
        lands: [],
      };
      this.branch?.landsOfWork.map(land => {
        const districtsDto = land.districts.map(district => {
          return {
            id: district.type,
            name: district.name_pl,
          };
        });
        branchLands.lands.push({
          id: land.id!,
          districts: districtsDto,
        });
      });
      await BranchApi.updateBranchLands(this.branchId, branchLands);
    } catch (e) {
      this.errorHandler(e);
    }
  }

  public goToUser(id: number) {
    if (this.$user.isAdmin || this.$user.isVerifier) {
      this.$goto.user(id);
    }
  }

  private setClones() {
    this.branchClone = cloneDeep(this.branch);
    this.branchHoursClone = cloneDeep(this.branchHours);
  }

  private async openSaveChangesModal(): Promise<boolean | undefined> {
    return await this.$modalService.open<boolean | undefined>(SaveChangesModal, {}, {
      maxWidth: 500,
      persistent: true,
      fullscreen: undefined,
    });
  }

  private async saveChanges() {
    await this.updateBranch();
    if (!isEqual(this.branchHours, this.branchHoursClone)) {
      await this.updateBranchHours();
    }

    if (!isEqual(this.branch?.landsOfWork, this.branchClone?.landsOfWork)) {
      await this.updateBranchLands();
    }

    if (this.branch && this.branchClone?.agents) {
      const diff = differenceBy(this.branchClone!.agents, this.branch!.agents, agent => agent.id);
      diff.map(async(agent: UserListElement) => {
        const user = new User();
        user.id = agent.id;
        user.quiz = new Quizzes();
        const userToSend = cloneDeep(user);
        if (agent.canManageProspects) {
          user!.privileges.canManageProspects = true;
          userToSend!.privileges.canManageProspects = false;
        }

        if (agent.canManageWidgets) {
          user!.privileges.canManage.widgets = true;
          userToSend!.privileges.canManage.widgets = false;
        }

        if (!isEqual(userToSend, user)) {
          user!.businessCardVisible = true;
          userToSend!.businessCardVisible = false;
          try {
            await UserApi.changeUserSetting(user!, userToSend!);
          } catch (e) {
            this.errorHandler(e, 'Wystąpił błąd podczas zapisu danych.');
          }
        }
      });
    }

    this.setClones();
    this.hasChanges = false;
  }

  private async undoChanges() {
    const response = await this.$modalService.open<boolean>(UndoChangesModal, {}, {
      maxWidth: 530,
      persistent: true,
      fullscreen: undefined,
    });

    if (response) {
      this.branch = cloneDeep(this.branchClone);
      this.branchHours = cloneDeep(this.branchHoursClone);
    }
  }
}
</script>

<style scoped lang="scss">
  .fixed-bottom-bar {
    position: fixed;
    bottom: 0px;
    z-index: 124;
    width: 100%;
    background-color: var(--v-primaryBg-base);
  }
  .has-changes {
    padding-bottom: 76px !important;
  }
  :deep(.v-input--checkbox) .v-input--selection-controls__ripple {
    z-index: 3 !important;
  }
</style>
